import { Component, Injector, Input, OnDestroy, OnInit } from '@angular/core'
import { FormControl } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { filter, finalize, first, switchMap, takeUntil, tap } from 'rxjs/operators'
import { Observable, of } from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { cloneDeep } from 'lodash'

import { User } from '@shared/models/user.model'
import { ClientsService } from '@app/modules/admin/clients/clients.service'
import { statuses } from '@app/core/constants/statuses'
import { RoleService } from '@shared/services/role.service'
import { Role } from '@shared/models/role.model'
import { PermissionsService } from '@shared/services/permissions.service'
import { DialogService } from '@shared/dialogs/dialog.service'
import { AuthService } from '@app/modules/auth/auth.service'
import { ClientUserListDatasource } from './client-user-list.datasource'
import { RoleType } from '@app/core/types/role.type'
import { AdminTeamService } from '@app/modules/admin/admin-team/admin-team.service'
import { Client } from '@shared/models/client.model'
import { sameId } from '@core/helpers/same-id'
import { ApiTableComponent } from '@shared/components/tables/api-table.component'
import { AvatarSize } from '../avatar/avatar.model'
import { PermissionSourceType, PermissionsSource } from '@core/types/permissions.source'
import { SETTINGS_FIELDS_REWRITE } from '@app/shared/components/tables/settings/fields-rewrite'

@Component({
  selector: 'lin-client-user-list',
  templateUrl: './client-user-list.component.html',
  providers: [
    {
      provide: SETTINGS_FIELDS_REWRITE,
      useValue: {
        last_logged_at: 'lastLoggedAt',
      },
    },
  ],
  standalone: false,
})
export class ClientUserListComponent extends ApiTableComponent implements OnInit, OnDestroy {
  roles: Role[]
  AvatarSize = AvatarSize
  dataSource: ClientUserListDatasource
  statuses = statuses.filter((status) => !status.hidden)
  isProcessing: { [id: string]: boolean | undefined } = {}
  clientId: string
  currentUser: User
  isManager: boolean

  @Input() view: 'driver' | 'client' = 'client'

  columns = [
    'picture',
    'fullName',
    'email',
    'driverId',
    'driverFobId',
    'role',
    'driverSensorId',
    'phone',
    'position',
    'last_logged_at',
    'status',
    'edit',
    'sendMessageLink',
    'impersonate',
    'archiveLink',
    'deleteLink',
  ]
  columnsMandatory = ['edit', 'sendMessageLink', 'impersonate', 'deleteLink', 'archiveLink']

  private _isEditMode: boolean
  private _isDeleteMode: boolean
  private _isAddMode: boolean

  get isClientView(): boolean {
    return this.view === 'client'
  }

  set isDeleteMode(isDeleteMode: boolean) {
    this._isDeleteMode = isDeleteMode
  }

  get isDeleteMode(): boolean {
    return this._isDeleteMode
  }

  set isEditMode(isEditMode: boolean) {
    this._isEditMode = isEditMode
  }

  get isEditMode(): boolean {
    return this._isEditMode
  }

  set isAddMode(isAddMode: boolean) {
    this._isAddMode = isAddMode
  }

  get isAddMode(): boolean {
    return this._isAddMode
  }

  fileName: string
  protected readonly omitStateFields = ['teamId']

  constructor(
    private clientsService: ClientsService,
    private dialog: DialogService,
    private roleService: RoleService,
    public authService: AuthService,
    private route: ActivatedRoute,
    private translateService: TranslateService,
    private permissionsService: PermissionsService,
    private adminTeamService: AdminTeamService,
    protected injector: Injector,
  ) {
    super(injector)
  }

  ngOnInit(): void {
    this.fileName = this.isClientView ? 'Client Users' : 'Drivers'
    this.authService.currentUser$.pipe(first()).subscribe((user) => {
      this.currentUser = user
      this.isManager = user.role.name === RoleType.Manager
    })
    super.ngOnInit()

    this.form.addControl('showArchived', new FormControl())

    if (!this.isClientView) this.filterColumns('role')
    // this hide the driverid - comment out to show the driverId
    // if (this.isClientView) {
    //   const filteredColumns = ['driverId']

    //   this.isManager && filteredColumns.push('edit')
    //   this.filterColumns(filteredColumns)
    // }

    this.roleService.clientTeamRoles$.pipe(takeUntil(this.onDestroy$)).subscribe((roles) => (this.roles = cloneDeep(roles)))
    this.roles.push(
      {
        displayName: 'Admin/Driver',
        name: 'admin-driver',
      },
      {
        displayName: 'Manager/Driver',
        name: 'manager-driver',
      },
    )

    this.clientId = this.route.parent.snapshot.params.id as string
    !this.clientId && this.filterColumns('impersonate')

    this.isEditMode = this.getPermission(!!this.clientId, 'EDIT')
    this.isDeleteMode = this.getPermission(!!this.clientId, 'DELETE')
    this.isAddMode = this.getAddPermission()
    !this.isEditMode && this.filterColumns('edit')
    !this.isDeleteMode && this.filterColumns('deleteLink')

    this.getClient()
      .pipe(
        tap((client: Client) => client && this.form.controls.teamId.setValue(client.team.id)),
        switchMap(() => this.authService.currentUser$),
        takeUntil(this.onDestroy$),
      )
      .subscribe((user) => {
        !this.clientId && this.form.controls.teamId.setValue(user.team.id)

        this.dataSource = new ClientUserListDatasource(
          this.clientId ? this.clientId : user.team.clientId,
          this.clientsService,
          this.adminTeamService,
          this.sort,
          this.paginator,
          this.form,
        )
      })
  }

  getClient(): Observable<Client | null> {
    return this.clientId ? this.clientsService.getClient(this.clientId) : of(null)
  }

  protected initForm(): void {
    super.initForm()

    this.form.addControl('teamId', new FormControl())
    this.form.addControl('driver_id', new FormControl())

    const roleFilter = !this.isClientView ? RoleType.Driver : null
    this.form.patchValue({ role: roleFilter })
  }

  canDeleteOrArchiveUser(user: User, action: 'DELETE' | 'ARCHIVE'): boolean {
    return this.getPermission(!!this.clientId, action) && !sameId(user.id, this.currentUser.id) && !this.isProcessing[user.id]
  }

  sendEmail(user: User): void {
    location.href = `mailto:${user.email}`
  }

  deleteUser(user: User): void {
    const confirmationMessage = this.translateService.instant('DIALOGS.MESSAGES.DELETE_USER') as string
    const errorMessage = this.translateService.instant('DIALOGS.MESSAGES.KEY_CONTACT_CAN_NOT_BE_DELETED') as string
    this.dialog
      .confirm(confirmationMessage)
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => (this.isProcessing[user.id] = true)),
        switchMap(() => this.clientsService.delete(user.id, user.team.clientId)),
        finalize(() => (this.isProcessing[user.id] = false)),
        switchMap(() => this.dataSource.refresh()),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        error: () => this.dialog.error(errorMessage).afterClosed(),
      })
  }

  archiveUser(user: User): void {
    const confirmationMessage = this.translateService.instant('DIALOGS.MESSAGES.ARCHIVE_USER') as string
    const errorMessage = this.translateService.instant('DIALOGS.MESSAGES.KEY_CONTACT_CAN_NOT_BE_ARCHIVED') as string
    this.dialog
      .confirm(confirmationMessage)
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => (this.isProcessing[user.id] = true)),
        switchMap(() => this.clientsService.archive(user.id)),
        finalize(() => (this.isProcessing[user.id] = false)),
        switchMap(() => this.dataSource.refresh()),
        takeUntil(this.onDestroy$),
      )
      .subscribe({
        error: () => this.dialog.error(errorMessage).afterClosed(),
      })
  }

  restoreUser(userId: number): void {
    this.isProcessing[userId] = true

    this.clientsService
      .restore(userId)
      .pipe(
        switchMap(() => this.dataSource.refresh()),
        finalize(() => (this.isProcessing[userId] = false)),
        takeUntil(this.onDestroy$),
      )
      .subscribe()
  }

  getEditUserLink(userId: string): string[] {
    return this.clientId ? [userId] : [userId, 'profile-info']
  }

  private hasPermission(permission: PermissionSourceType): boolean {
    return this.permissionsService.hasPermissionSync(permission)
  }

  private getPermission(isClientId: boolean, action: 'EDIT' | 'DELETE' | 'ADD' | 'ARCHIVE'): boolean {
    if (isClientId) {
      return true
    } else if (!this.isClientView) {
      return this.hasPermission(PermissionsSource[`DRIVERS.DRIVERS_LIST.${action}`])
    } else {
      return this.hasPermission(PermissionsSource[`CONFIGURATION.USERS_LIST.${action}`])
    }
  }

  private getAddPermission(): boolean {
    if (this.isClientView && this.isManager) return false

    return this.getPermission(!!this.clientId, 'ADD')
  }
}
